import React, { useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Field, FieldArray } from 'formik'
import {
  IconArrowBackUp,
  IconArrowsSplit2,
  IconCopy,
  IconGitBranch,
  IconListSearch,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react'
import { isEmpty, isNil, omit } from 'lodash'
import classNames from 'classnames'

import { TextField } from '@/components/forms/formik'
import Select from '@/components/forms/Select'
import { LlmModelSelect } from '@/components/forms/formik/selects/LlmModelSelect'
import { Drag, Drop, DragAndDrop } from '@/components/dragDrop'
import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import { toast } from '@/components/toasts/toast.helpers'

import {
  buildKeyUsageCounts,
  getAvailableFieldNames,
  getDefaultFieldsByFieldName,
  getDuplicateFieldKeys,
  hasSwitchedTaxonomy,
  inheritsTaxonomyFromDefault,
  normalizeFieldKey,
  getRowTypeKey,
  getTypeKeyLabel,
  isReuseTypeMismatch,
  isTaxonomyDrivenMetadataField,
  isLogicField,
  LOGIC_COMBINATOR_COPY,
  PICK_LIST_TYPE_KEY,
} from '../helpers'
import {
  FIELD_GROUP,
  partitionFieldsByAgentVisibility,
  moveFieldBetweenVisibilityGroups,
  appendFieldInVisibilityOrder,
} from '../helpers/fieldGroups.helpers'
import { countOutgoing, isHubSource } from '../helpers/connectionRail.helpers'
import {
  describeTokenProblems,
  findTokenProblems,
  getConditionsForTarget,
  hasFieldReferences,
  getTokenSources,
  getTokenSuggestions,
  withTokenLinks,
} from '../helpers/fieldTokens.helpers'
import {
  addLink,
  buildOptionNameIndex,
  buildOptionUuidsByFieldKey,
  buildPendingOptionLabels,
  getEligibleSources,
  getGatedOperands,
  getMoveBlockedReason,
  removeFieldFromLinks,
  renameFieldInLinks,
  resolveStagedOperands,
} from '../helpers/fieldLinks.helpers'
import { FieldConditionModal } from './FieldConditionModal'
import { FieldLinkStrip } from './FieldLinkStrip'
import { ConnectionRail } from './ConnectionRail'
import { CopyFieldDescriptionModal } from './CopyFieldDescriptionModal'
import { PickListOptionsModal } from './PickListOptionsModal'
import { CategorizationOptionsModal } from './CategorizationOptionsModal'
import { PromptDescriptionField } from './PromptDescriptionField'
import { PromptEditorFields } from './PromptEditorFields'
import { AddFieldModal } from './AddFieldModal'
import { StagedOptionsModal } from './StagedOptionsModal'
import { FieldTypePill } from './FieldTypePill'
import { LogicCombinator } from './LogicCombinator'

const INHERITED_FIELD_TOOLTIP =
  'This field inherits its description from your organization defaults.'

// A voip_metadata field's description is the path its value is read from — a
// short key, not prose — so the row names it and asks for it in one line.
const METADATA_PATH_FIELD_PROPS = {
  singleLine: true,
  label: 'Metadata Path',
  emptyMessage: 'Metadata path cannot be empty',
  placeholder: 'e.g. caller.region',
}

const VISIBILITY_GROUPS = [
  {
    id: FIELD_GROUP.SHOWN_TO_AGENT,
    title: 'Shown to Agent',
    description:
      'Processed as soon as the call ends - fields that are necessary for the agent to see.',
  },
  {
    id: FIELD_GROUP.PROCESSED_LATER,
    title: 'Processed Later',
    description: 'Processed shortly after the call ends - generally more accurate.',
  },
]

// The draggable key/value rows shared by the playbook prompt editor and the
// org defaults editor. Rows flagged with `inheritsDefault` keep their name and
// description locked — those values come from the org default flow and must be
// edited there. An option-backed field's options are locked the same way and for
// a stronger reason: the taxonomy itself is the default's, so saving options from
// a playbook would change them for every playbook that inherits the field. Such
// a row can view its options, and can fork a copy of them into this configuration
// alone.
//
// That lock holds however the row came to share those options — inherited on
// load, created under a default's name, or linked back after a fork — so the
// org defaults editor stays the only place a default's options can be changed.
//
// With `groupByAgentVisibility`, rows render in two labeled groups by
// show_to_user, and dragging a row across groups flips the flag. The playbook
// editor sets it on the call channel only — the one that honors the flag — while
// the org defaults editor, being channel-less, always groups.
//
// Both editors create fields the same way — through the guided add-field modal,
// which can create any field type, plus the option editors its option-backed
// rows need. An org default field is a bit field like any other, so nothing
// about creation is playbook-specific.
//
// `enableFieldTools` turns on the affordances that only mean something against a
// playbook flow: the field-name reuse dropdown, copy-this-description-to-other-
// playbooks, per-field individual execution, and applying unsaved options to a
// test run. The org defaults editor leaves it off — it has no other playbook to
// copy to and no test runner to apply options for.
//
// `isOrgDefaultFlow` says these rows belong to the org's default flow rather
// than a playbook's: the taxonomies here are the ones playbooks inherit, so the
// option editors stay open but say how far a change reaches.
//
// `readOnly` renders the rows as a record rather than a form — names and
// descriptions are locked, and every control that would change the
// configuration (add, delete, reorder, copy, options, individual execution) is
// gone. That's how an older configuration version is shown.
export const PromptFieldRows = ({
  setFieldValue,
  values,
  flags,
  groupByAgentVisibility = false,
  enableFieldTools = false,
  isOrgDefaultFlow = false,
  readOnly = false,
}) => {
  const {
    rtnFieldNames = [],
    rtnBitFlow,
    rtnOrg,
    rtnModelOptions = [],
    rtnOrgDefaultFlow,
  } = useSelector((state) => state.playbook)
  const playbooks = useSelector((state) => state.analytics?.data?.playbooks) ?? []
  const currentUserOrgId = useSelector((state) => state.currentUser?.organizationid)
  const rtnModelOptionsLoading = useSelector(
    (state) => state.prompts?.loading?.rtnModelOptions ?? false
  )
  // Per-field prompt/model selection ("run on its own prompt") is the one
  // remaining internal affordance — field creation and option editing are open
  // to every org that can reach the editor.
  const isAdmin = currentUserOrgId === 1

  // Shape the model list for the per-field LlmModelSelect (mirrors TestPromptForm).
  const formattedModelOptions = rtnModelOptions.map((option) => ({
    value: option.llm_model_id,
    label: option.display_name,
    description: option.description,
    deprecated: option.deprecated,
  }))
  // Taxonomy edits (pick_list + categorization) target the org being viewed
  // (admins pick one via rtnOrg) or the user's own org.
  const taxonomyOrgId = rtnOrg || currentUserOrgId
  // What each field name already means at the org level: the type, description
  // and options a new field under that name should start from, and the taxonomy a
  // row that holds its own copy would return to. In the defaults editor a row IS
  // the entry listed here, so nothing reads as an override.
  const defaultFieldsByName = getDefaultFieldsByFieldName(rtnOrgDefaultFlow)

  // The field being copied to other playbooks / having its options viewed.
  const [copyField, setCopyField] = useState(null)
  const [pickListField, setPickListField] = useState(null)
  const [categorizationField, setCategorizationField] = useState(null)
  // A new (unsaved) taxonomy field whose staged options are being edited.
  const [stagedOptionsField, setStagedOptionsField] = useState(null)
  // The guided add-field modal, plus which visibility group the add button
  // that opened it belongs to.
  const [showAddField, setShowAddField] = useState(false)
  const [addFieldShowToAgent, setAddFieldShowToAgent] = useState(null)
  // The row whose gating condition is being added or edited:
  // { field, link } — link absent when adding a new condition.
  const [conditionDraft, setConditionDraft] = useState(null)
  const [focusedFieldKey, setFocusedFieldKey] = useState(null)
  const fieldListRef = useRef(null)

  // Unsaved taxonomy edits applied for testing, keyed by taxonomy_uuid. Each
  // holds the modal's { root, options } so the test can derive inline options
  // and a later "save taxonomy changes" can persist them with option uuids
  // preserved. Kept off prompt_fields so they don't read as field changes or
  // get saved with the flow. (A discard drops them all the same — they're
  // unsaved work — but as their own step, not as a row edit.)
  const pendingTaxonomies = values.pending_taxonomies ?? {}
  const applyPendingTaxonomy = (taxonomyUuid, taxonomyEdit) =>
    setFieldValue('pending_taxonomies', { ...pendingTaxonomies, [taxonomyUuid]: taxonomyEdit })
  const clearPendingTaxonomy = (taxonomyUuid) =>
    setFieldValue('pending_taxonomies', omit(pendingTaxonomies, taxonomyUuid))

  // Targets for the copy modal: every other playbook (the source can't copy to
  // itself). The API skips/errors any that don't share the field.
  const copyTargetOptions = playbooks.filter(
    (playbook) => playbook.value !== values.selected_playbook_cid
  )

  // Saved field state keyed by field_id, so we can tell whether a row has
  // unsaved edits — copy sends the *saved* description, so editing the name or
  // description in the form must disable copy until the change is saved.
  const savedFieldsByFieldId = {}
  if (rtnBitFlow?.fields) {
    rtnBitFlow.fields.forEach((field) => {
      savedFieldsByFieldId[field.field_id] = field
    })
  }

  const isFieldEdited = (promptField) => {
    const savedField = savedFieldsByFieldId[promptField.field_id]
    return (
      !savedField ||
      savedField.field_name !== promptField.key ||
      savedField.description !== promptField.value
    )
  }

  // Memoized so the per-row option filter is O(names) instead of O(names × rows)
  // on every render (it runs for every row on each keystroke-driven re-render).
  const keyUsageCounts = useMemo(
    () => buildKeyUsageCounts(values.prompt_fields),
    [values.prompt_fields]
  )

  // Org field names not already used by another row — lets the user reuse an
  // existing name (kept consistent across playbooks) or enter a brand-new one.
  const getFieldNameOptions = (currentIndex) =>
    getAvailableFieldNames(rtnFieldNames, keyUsageCounts, values.prompt_fields[currentIndex]?.key)

  // Same reuse list for the add-field modal (a new field has no current key, so
  // every name already in this playbook is excluded), plus those used names so
  // the modal can block a free-text duplicate.
  const addFieldNameOptions = getAvailableFieldNames(rtnFieldNames, keyUsageCounts)
  const usedFieldNames = values.prompt_fields
    .map((field) => normalizeFieldKey(field.key))
    .filter(Boolean)

  // A field name may only appear once per configuration; flag the offenders so
  // the rows can show an error and Save can be blocked.
  const duplicateFieldKeys = getDuplicateFieldKeys(values.prompt_fields)

  // Gating conditions between fields, held flat so reordering and dragging
  // between visibility groups can't corrupt them (they key off field name, the
  // same way the wire shape does).
  // Normalized once here rather than at each use: this component is mounted by
  // the playbook editor, the defaults editor and the version viewer, and not
  // every caller seeds the key.
  const fieldLinks = values.field_links ?? []
  // Gating conditions are behind a flag. Only what the editor DRAWS is hidden:
  // the flow keeps whatever edges it already has, and the rules that stop a save
  // from becoming invalid (a cross-group drag, an option a gate reads) still
  // read the real list — hiding a feature must not quietly drop the data or the
  // protections behind it.
  const branchingEnabled = flags?.notetakerBranchingEnabled === true
  const shownLinks = branchingEnabled ? fieldLinks : []
  // The rail draws both kinds — a value-carrying edge is a real dependency and
  // worth seeing — so it takes the conditions plus the edges the descriptions
  // imply. Those aren't in field_links until save, so they're derived here.
  // The names the flow loaded with — what the descriptions were written
  // against, and so the only way to tell a reference broken by a rename from a
  // dollar sign that was always prose.
  const loadedFieldNames = (rtnBitFlow?.fields ?? []).map((field) => field.field_name)
  // Recomputed, not accumulated: appending the derived edges to the loaded ones
  // left a dashed arrow on screen after its `$token` was deleted, since nothing
  // prunes the persisted edge until the save does.
  const shownRailLinks = branchingEnabled ? withTokenLinks(values.prompt_fields, shownLinks) : []
  // A gate stores an option's category uuid; the org field list is what turns
  // those back into names for the chips.
  // Names for the chips. Pending options are folded in so a gate written against
  // an option that only exists in an unsaved edit prints that option's name
  // rather than reading as one that no longer exists.
  const optionNames = {
    ...buildOptionNameIndex(rtnFieldNames),
    ...buildPendingOptionLabels(pendingTaxonomies),
  }
  // The rows whose options are the org default's, by name. A gate can't read
  // those — see INHERITED_OPTIONS_GATE_REASON — so the condition editor offers
  // them greyed out with the reason rather than hiding them.
  const inheritedTaxonomyKeys = new Set(
    values.prompt_fields
      .filter((field) => inheritsTaxonomyFromDefault(field, defaultFieldsByName, isOrgDefaultFlow))
      .map((field) => normalizeFieldKey(field.key))
  )
  const setFieldLinks = (nextLinks) => setFieldValue('field_links', nextLinks)

  // A gate on an option a pending edit added holds the option's name, and only
  // the pending edit marks that name as unsaved. The modal's save just minted
  // the uuid, so swap it in before the edit goes, or the gate would reach the
  // flow save as a bare name with nothing left to stop it.
  const handleTaxonomySaved = (taxonomyUuid, savedTaxonomy) => {
    setFieldLinks(
      resolveStagedOperands(
        fieldLinks,
        values.prompt_fields,
        buildOptionUuidsByFieldKey(values.prompt_fields, { [taxonomyUuid]: savedTaxonomy })
      )
    )
    clearPendingTaxonomy(taxonomyUuid)
  }

  const handleReorder = (result) => {
    const { source, destination } = result
    // Belt and braces: Drag already refuses to start a drag when readOnly, so
    // this can't fire — but reordering is a field mutation like any other, and
    // shouldn't depend on the drag layer to stay locked.
    if (readOnly || !destination) {
      return
    }

    if (groupByAgentVisibility) {
      // Crossing groups flips show_to_user, which can invert a dependency the
      // execution order doesn't allow — an agent-visible field can never wait
      // on one processed later. Refuse the drop rather than save a flow that
      // would silently delay the agent's fields.
      if (source.droppableId !== destination.droppableId) {
        const partitioned = partitionFieldsByAgentVisibility(values.prompt_fields)
        const dragged = partitioned[source.droppableId][source.index]?.field
        const blockedReason = isNil(dragged)
          ? ''
          : getMoveBlockedReason(
              values.prompt_fields,
              fieldLinks,
              dragged,
              destination.droppableId === FIELD_GROUP.SHOWN_TO_AGENT
            )
        if (!isEmpty(blockedReason)) {
          toast.error(blockedReason)
          return
        }
      }

      const newOrder = moveFieldBetweenVisibilityGroups(values.prompt_fields, source, destination)
      setFieldValue('prompt_fields', newOrder)
      return
    }

    // Clone before swapping so we don't mutate Formik's array in place.
    const newOrder = [...values.prompt_fields]
    const valueAtSource = newOrder[source.index]
    newOrder[source.index] = newOrder[destination.index]
    newOrder[destination.index] = valueAtSource

    setFieldValue('prompt_fields', newOrder)
  }

  // Linking a saved fork back to the org default: the row points at the
  // default's taxonomy again, and the save sends that uuid so the field stops
  // diverging. The copy it was using is left behind — its own options go with
  // it, which is why the affordance says so before it's used.
  const linkTaxonomyToDefault = (index, defaultTaxonomyUuid) => {
    const promptField = values.prompt_fields[index]
    // Going back to the default's options would put a gate on options this
    // editor doesn't own — the state the condition editor refuses to create in
    // the first place. Refuse it here too rather than leave a gate the defaults
    // can quietly break.
    if (countOutgoing(fieldLinks, promptField.key) > 0) {
      toast.error(
        `Other fields are gated on ${promptField.key}. Remove those conditions before using your organization default options.`
      )
      return
    }
    setFieldValue(`prompt_fields.${index}`, {
      ...omit(promptField, ['new_taxonomy_options']),
      taxonomy_uuid: defaultTaxonomyUuid,
      // Back on the default's options means back to inheriting them: they're
      // shared again, so this editor stops being a place they can be changed.
      inheritsTaxonomy: true,
    })

    // Linking back is rarely the end of the thought — the next question is what
    // the default's options actually are, and often whether to fork them and
    // edit. So re-target whichever viewer asked for this at the default's
    // taxonomy, read-only, rather than closing and making the user open it
    // again. Triggered from the row's pill instead, there's nothing open to
    // re-target.
    const relinked = { taxonomyUuid: defaultTaxonomyUuid, inheritsTaxonomy: true }
    if (!isNil(pickListField)) {
      setPickListField({ ...omit(pickListField, ['defaultTaxonomyUuid']), ...relinked })
    }
    if (!isNil(categorizationField)) {
      setCategorizationField({ ...omit(categorizationField, ['defaultTaxonomyUuid']), ...relinked })
    }
  }

  // `index` is the field's position in the flat prompt_fields array (formik
  // bindings); `position` is its index within the droppable it renders in.
  const renderRow = (arrayHelpers, promptField, index, position) => {
    const isSummary = promptField.key === 'Summary'
    const isSummaryEditable = flags?.summaryEditEnabled === true
    const inheritsDefault = promptField.inheritsDefault === true
    const keyDisabled = isSummary || inheritsDefault || readOnly
    const isDuplicateName = duplicateFieldKeys.has(normalizeFieldKey(promptField.key))
    // A voip_metadata field's description is the metadata key the backend reads
    // off the call, not prompt instructions — so the row asks for a key.
    const isVoipMetadata = promptField.field_type === 'voip_metadata'
    // A logic field's description is its combinator, and its conditions are the
    // terms it combines rather than gates on whether it runs.
    const isLogic = isLogicField(promptField)
    // One is option-backed when it was given the Pick List data type: its
    // options live in a taxonomy, names only, like a pick_list's.
    const isOptionBackedVoip = isTaxonomyDrivenMetadataField(promptField)
    // The options belong to the org default and are shared by every inheriting
    // playbook, so this editor shows them as a record until the row forks them.
    const savedField = savedFieldsByFieldId[promptField.field_id]
    // An options change the configuration save carries: the row points at a
    // different taxonomy than the flow loaded, or at none because a copy is
    // staged instead. Marked and confirmed like any other unsaved option edit.
    const switchedTaxonomy = hasSwitchedTaxonomy(promptField, savedField)
    const defaultTaxonomyUuid =
      defaultFieldsByName[normalizeFieldKey(promptField.key)]?.taxonomy_uuid
    // Whether these options are shared with the org default (see
    // inheritsTaxonomyFromDefault) — the same answer the condition editor asks
    // for about every row, which is why it lives in the helpers.
    const inheritsTaxonomy = inheritsTaxonomyFromDefault(
      promptField,
      defaultFieldsByName,
      isOrgDefaultFlow
    )
    // The other side of that comparison: the field holds its own taxonomy where
    // the org default has a different one — a fork, once saved. Nothing else on
    // the row says so, and without it there'd be no way back to the default.
    const overridesDefaultTaxonomy =
      !isNil(defaultTaxonomyUuid) &&
      !isNil(promptField.taxonomy_uuid) &&
      defaultTaxonomyUuid !== promptField.taxonomy_uuid
    const isPickList =
      (promptField.field_type === 'pick_list' || isOptionBackedVoip) && promptField.taxonomy_uuid
    // Categorization fields own a taxonomy too, edited with the same options
    // viewer (capped at 30 options, each requiring a description).
    const isCategorization =
      promptField.field_type === 'categorization' && promptField.taxonomy_uuid
    // A freshly added (unsaved) option-backed field has no taxonomy yet — its
    // options are staged on the row and editable in place before the flow is
    // saved (which mints the taxonomy).
    const isNewTaxonomyField =
      (promptField.field_type === 'pick_list' ||
        promptField.field_type === 'categorization' ||
        isOptionBackedVoip) &&
      !promptField.taxonomy_uuid
    // Whether this field's taxonomy has unsaved options applied for testing.
    const hasPendingTaxonomy = !isEmpty(pendingTaxonomies[promptField.taxonomy_uuid])
    // Admins can run an extraction field on its own prompt/model instead of the
    // flow's shared one. pick_list fields already own their prompt, so they're
    // excluded; the toggle reflects whether this field diverges from the shared.
    const canConfigureIndividualPrompt =
      enableFieldTools && isAdmin && promptField.field_type === 'extraction'

    const replaceField = (nextField) => {
      arrayHelpers.replace(index, nextField)
    }

    // Links are keyed by field name, so a rename has to move both ends or the
    // condition is orphaned.
    const renameField = (nextKey) => {
      if (nextKey !== promptField.key && !isEmpty(promptField.key)) {
        setFieldLinks(renameFieldInLinks(fieldLinks, promptField.key, nextKey))
      }
      replaceField({ ...promptField, key: nextKey })
    }

    // A condition can't survive its source, and the row's own conditions go
    // with it.
    const removeField = () => {
      setFieldLinks(removeFieldFromLinks(fieldLinks, promptField.key))
      arrayHelpers.remove(index)
    }

    const rowLinks = getConditionsForTarget(shownLinks, promptField.key)
    const tokenSources = branchingEnabled
      ? getTokenSources(promptField, values.prompt_fields, shownLinks)
      : []
    // Copying a description that reads another field's answer would land a
    // reference the receiving flow has no edge for — and may have no such field
    // for either. Nothing here can create those edges or guarantee the inputs
    // exist there, so the copy is refused rather than quietly shipping dead
    // text. Stated before the unsaved-changes reason, since saving won't help.
    const getCopyDisabledReason = () => {
      if (branchingEnabled && hasFieldReferences(promptField.value)) {
        return "This description uses another field's answer, so it can't be copied"
      }
      if (!promptField.field_id || !rtnBitFlow?.flow_uuid || isFieldEdited(promptField)) {
        return isVoipMetadata
          ? 'Save your changes before copying this metadata path to other playbooks'
          : 'Save your changes before copying this description to other playbooks'
      }
      return null
    }
    const copyDisabledReason = getCopyDisabledReason()
    // Only a named row can be gated — a link needs both endpoints by name — and
    // only when something upstream is legal to depend on.
    const eligibleSources = isEmpty((promptField.key ?? '').trim())
      ? []
      : getEligibleSources(values.prompt_fields, shownLinks, promptField)
    const canAddCondition = enableFieldTools && branchingEnabled && !isEmpty(eligibleSources)
    const getAddConditionTooltip = () => {
      if (isLogic) {
        return 'Add a condition this field combines'
      }
      return isEmpty(rowLinks)
        ? "Only run this field when another field's value matches"
        : 'Add another condition'
    }
    // A field the flow depends on: deleting it takes those conditions with it,
    // so the row says so before the click.
    const dependentCount = countOutgoing(shownRailLinks, promptField.key)
    // A field gating more than a handful has its rail lines suppressed — they'd
    // fuse into a band — so the count is the only place that reach shows.
    const railSuppressed = isHubSource(shownRailLinks, promptField.key)

    // Reuse is same-type only: only org names of this row's display type are
    // offered — changing a field's type is done by creating a new field, not via
    // reuse. A blank/new row is a Text field until saved, so it offers Text
    // names. When none match, the dropdown says so (see noOptionsMessage).
    const rowTypeKey = getRowTypeKey(promptField)
    const rowTypeLabel = getTypeKeyLabel(rowTypeKey)
    const reusableFieldNameOptions = getFieldNameOptions(index).filter(
      (option) => !isReuseTypeMismatch(option, rowTypeKey)
    )

    // Each dropdown option shows its type pill. The selected value in the
    // control keeps just its name (the row already renders a type pill).
    const formatFieldNameOption = (option, meta) => {
      if (meta.context !== 'menu') {
        return option.label
      }
      return (
        <span className="rtn-field-name-option">
          <span>{option.label}</span>
          <FieldTypePill displayType={option.displayType} />
        </span>
      )
    }

    const toggleIndividualExecution = () => {
      if (promptField.individualExecution) {
        // Back to the shared prompt — drop the per-field prompt keys so the row
        // matches a non-individual field again.
        arrayHelpers.replace(index, omit(promptField, ['individualExecution', 'llm_model_id', 'system_prompt', 'user_prompt', 'max_response_tokens'])) /* prettier-ignore */
      } else {
        // Default the field's model and prompt to the shared prompt's (the
        // Default AI Model) — the backend mints a copy of that prompt, so this
        // matches what the field actually runs until the admin changes it.
        arrayHelpers.replace(index, {
          ...promptField,
          individualExecution: true,
          llm_model_id: promptField.llm_model_id ?? values.llm_model_id ?? null,
          system_prompt: promptField.system_prompt ?? values.system_prompt ?? '',
          user_prompt: promptField.user_prompt ?? values.user_prompt ?? '',
          max_response_tokens:
            promptField.max_response_tokens ?? values.max_response_tokens ?? null,
        })
      }
    }

    return (
      <Drag
        key={`${index}`}
        draggableId={`${index}`}
        index={position}
        outer
        alwaysShowIcon
        showDragStyle
        readOnly={readOnly}
      >
        <div
          className="rtn-pair"
          data-field-key={promptField.key}
          onMouseEnter={() => setFocusedFieldKey(promptField.key)}
          onMouseLeave={() => setFocusedFieldKey(null)}
        >
          <div className="rtn-key-container">
            <div className="rtn-key-name">
              {enableFieldTools ? (
                <div
                  className={classNames('rtn-pair-key', {
                    disabled: keyDisabled,
                    error: isDuplicateName,
                  })}
                  data-testid={`prompt_fields.${index}.key`}
                >
                  <Select
                    allowFreeText
                    commitFreeTextOnBlur
                    options={reusableFieldNameOptions}
                    placeholder="Select existing or enter a new name"
                    value={promptField.key}
                    onChange={(option) => renameField((option?.value || '').trim())}
                    noOptionsMessage={() =>
                      `No other ${rowTypeLabel} fields exist to reuse, enter a new name`
                    }
                    formatOptionLabel={formatFieldNameOption}
                    isClearable
                    fixedWidth
                    isDisabled={keyDisabled}
                  />
                </div>
              ) : (
                <Field
                  name={`prompt_fields.${index}.key`}
                  component={TextField}
                  className={classNames('rtn-pair-key', { error: isDuplicateName })}
                  onKeyUp={(event) => {
                    renameField(event.target.value)
                  }}
                  data-testid={`prompt_fields.${index}.key`}
                  placeholder="Name your call element"
                  disabled={keyDisabled}
                />
              )}
              {dependentCount > 0 && (
                <Pill
                  xs
                  outlined
                  className="rtn-fanout-pill"
                  tooltip={
                    railSuppressed
                      ? `${dependentCount} fields are gated on this one - too many to draw`
                      : `${dependentCount} ${dependentCount === 1 ? 'field is' : 'fields are'} gated on this one`
                  }
                  dataTestId={`field-fanout-${index}`}
                >
                  {`\u2192 ${dependentCount}`}
                </Pill>
              )}
              <FieldTypePill
                fieldType={promptField.field_type}
                dataType={promptField.data_type}
                // An option-backed metadata field reads as a Pick List even
                // though its data_type is string — the taxonomy is what resolves
                // its value — matching how the row gates its name options.
                {...(isOptionBackedVoip ? { typeKey: PICK_LIST_TYPE_KEY } : {})}
              />
            </div>
            {isDuplicateName && (
              <span
                className="status-critical rtn-duplicate-name-error"
                data-testid={`duplicate-name-error-${index}`}
              >
                Field name must be unique
              </span>
            )}
            {!readOnly && (
              <div className="rtn-row-actions">
                {inheritsDefault && (
                  <Button
                    square
                    basic
                    small
                    tooltip="Edit this field for this configuration only"
                    onClick={() => {
                      // Unlock the row for a flow-specific override. The backend
                      // stores whatever diverges from the org default as an
                      // override on save, so unlocking is purely a UI
                      // affordance — saving unchanged values keeps the field
                      // inheriting. Snapshot the inherited values so the row can
                      // be returned to the default before saving.
                      arrayHelpers.replace(index, {
                        ...promptField,
                        inheritsDefault: false,
                        unlockedFromDefault: true,
                        defaultKey: promptField.key,
                        defaultValue: promptField.value,
                      })
                    }}
                    data-testid={`edit-row-button-${index}`}
                  >
                    <IconPencil data-testid="edit-row-button" />
                  </Button>
                )}
                {promptField.unlockedFromDefault === true && (
                  <Button
                    square
                    basic
                    small
                    tooltip="Return to organization default"
                    onClick={() => {
                      // Drop the transient unlock keys entirely (not set to
                      // undefined) so the restored row deep-equals its loaded
                      // snapshot — otherwise the editor still reads this as an
                      // unsaved change.
                      replaceField({
                        ...omit(promptField, ['unlockedFromDefault', 'defaultKey', 'defaultValue']),
                        key: promptField.defaultKey,
                        value: promptField.defaultValue,
                        inheritsDefault: true,
                      })
                    }}
                    data-testid={`revert-row-button-${index}`}
                  >
                    <IconArrowBackUp data-testid="revert-row-button" />
                  </Button>
                )}
                {isPickList && (
                  <Button
                    square
                    basic
                    small
                    type="button"
                    className={classNames({ 'rtn-inherited-lock': inheritsTaxonomy })}
                    tooltip={
                      inheritsTaxonomy
                        ? "View this field's options, shared from your organization defaults"
                        : "View this field's options"
                    }
                    onClick={() =>
                      setPickListField({
                        index,
                        fieldName: promptField.key,
                        gatedOperands: getGatedOperands(fieldLinks, promptField.key),
                        taxonomyUuid: promptField.taxonomy_uuid,
                        inheritsTaxonomy,
                        // Present only when there's a default to go back to.
                        ...(overridesDefaultTaxonomy ? { defaultTaxonomyUuid } : {}),
                        // A test run can only resolve unsaved options for a real
                        // pick_list field — a metadata field's taxonomy is read
                        // from the DB — so only it is offered "apply for testing".
                        inlineTestable: promptField.field_type === 'pick_list',
                      })
                    }
                    data-testid={`pick-list-options-button-${index}`}
                  >
                    <IconListSearch data-testid="pick-list-options-button" />
                  </Button>
                )}
                {isCategorization && (
                  <Button
                    square
                    basic
                    small
                    type="button"
                    className={classNames({ 'rtn-inherited-lock': inheritsTaxonomy })}
                    tooltip={
                      inheritsTaxonomy
                        ? "View this field's options, shared from your organization defaults"
                        : "View this field's options"
                    }
                    onClick={() =>
                      setCategorizationField({
                        index,
                        fieldName: promptField.key,
                        gatedOperands: getGatedOperands(fieldLinks, promptField.key),
                        taxonomyUuid: promptField.taxonomy_uuid,
                        inheritsTaxonomy,
                        // Present only when there's a default to go back to.
                        ...(overridesDefaultTaxonomy ? { defaultTaxonomyUuid } : {}),
                      })
                    }
                    data-testid={`categorization-options-button-${index}`}
                  >
                    <IconListSearch data-testid="categorization-options-button" />
                  </Button>
                )}
                {isNewTaxonomyField && (
                  <Button
                    square
                    basic
                    small
                    type="button"
                    tooltip="Edit this field's options"
                    onClick={() =>
                      setStagedOptionsField({
                        index,
                        fieldName: promptField.key,
                        gatedOperands: getGatedOperands(fieldLinks, promptField.key),
                        isCategorization: promptField.field_type === 'categorization',
                        options: promptField.new_taxonomy_options ?? [],
                      })
                    }
                    data-testid={`edit-options-button-${index}`}
                  >
                    <IconListSearch data-testid="edit-options-button" />
                  </Button>
                )}
                {(switchedTaxonomy || hasPendingTaxonomy) && (
                  <Pill
                    xs
                    outlined
                    clickable
                    tooltip={
                      switchedTaxonomy
                        ? 'This field uses different options than the saved configuration - click to undo'
                        : 'Unsaved options applied for testing - click to clear'
                    }
                    onClick={() => {
                      if (!switchedTaxonomy) {
                        clearPendingTaxonomy(promptField.taxonomy_uuid)
                        return
                      }
                      // Back to what the flow loaded, down to the inherited flag,
                      // so the row deep-equals its snapshot again — otherwise
                      // the editor still reads this as an unsaved change.
                      replaceField({
                        ...omit(promptField, ['new_taxonomy_options', 'inheritsTaxonomy']),
                        ...(isNil(savedField.taxonomy_uuid)
                          ? {}
                          : { taxonomy_uuid: savedField.taxonomy_uuid }),
                        ...(savedField.inherited_attributes?.includes('taxonomy_uuid') === true
                          ? { inheritsTaxonomy: true }
                          : {}),
                      })
                    }}
                    dataTestId={`unsaved-options-${index}`}
                  >
                    Unsaved options
                  </Pill>
                )}
                {canConfigureIndividualPrompt && (
                  <Button
                    basic
                    small
                    square
                    type="button"
                    active={promptField.individualExecution === true}
                    tooltip="Run with its own prompt"
                    onClick={toggleIndividualExecution}
                    data-testid={`individual-execution-toggle-${index}`}
                  >
                    <IconArrowsSplit2 data-testid="individual-execution-toggle" />
                  </Button>
                )}
                {/* A combinator means nothing without the conditions it
                    combines, which don't travel with a copy. */}
                {enableFieldTools && !isLogic && (
                  <Button
                    disabled={!isNil(copyDisabledReason)}
                    disabledTooltip={copyDisabledReason}
                    tooltip={
                      isVoipMetadata
                        ? 'Copy metadata path to other playbooks'
                        : 'Copy this description to other playbooks'
                    }
                    basic
                    small
                    square
                    onClick={() =>
                      setCopyField({ fieldName: promptField.key, fieldId: promptField.field_id })
                    }
                    data-testid={`copy-row-button-${index}`}
                  >
                    <IconCopy data-testid="copy-row-button" />
                  </Button>
                )}
                {enableFieldTools && branchingEnabled && (
                  <Button
                    basic
                    small
                    square
                    type="button"
                    disabled={!canAddCondition}
                    disabledTooltip={
                      isEmpty((promptField.key ?? '').trim())
                        ? 'Name this field before gating it on another'
                        : 'No other field can gate this one'
                    }
                    tooltip={getAddConditionTooltip()}
                    onClick={() => setConditionDraft({ field: promptField })}
                    data-testid={`add-condition-button-${index}`}
                  >
                    <IconGitBranch data-testid="add-condition-button" />
                  </Button>
                )}
                <Button
                  disabled={isSummary}
                  disabledTooltip={
                    isSummaryEditable
                      ? 'You cannot delete the Summary row'
                      : 'You cannot edit or delete the Summary row'
                  }
                  basic
                  small
                  square
                  tooltip={
                    dependentCount > 0
                      ? `Delete this field, and the ${dependentCount === 1 ? 'condition' : `${dependentCount} conditions`} that depend on it`
                      : undefined
                  }
                  onClick={removeField}
                  data-testid={`delete-row-button-${index}`}
                >
                  <IconTrash data-testid="delete-row-button" />
                </Button>
              </div>
            )}
          </div>

          {enableFieldTools && !isLogic && (
            <FieldLinkStrip
              promptFields={values.prompt_fields}
              links={rowLinks}
              tokenSources={tokenSources}
              optionNames={optionNames}
              index={index}
              readOnly={readOnly}
              onEdit={(link) => setConditionDraft({ field: promptField, link })}
              canAdd={canAddCondition}
              onAdd={() => setConditionDraft({ field: promptField })}
              onRemove={(link) =>
                setFieldLinks(fieldLinks.filter((candidate) => candidate !== link))
              }
            />
          )}

          {/* Description edits stay local while typing and sync to Formik on a
              short debounce / on blur, so a keystroke doesn't re-render the whole
              field list (see PromptDescriptionField). A logic field's description
              is one of two fixed values, so it's picked rather than typed. */}
          {isLogic ? (
            // The combinator and the conditions it combines read as one
            // statement, with the joining word drawn between the conditions so
            // the row says exactly how its inputs are applied.
            <div className="rtn-logic-combinator" data-testid={`logic-combinator-${index}`}>
              <label>Combine conditions with</label>
              <LogicCombinator
                small
                value={promptField.value}
                onChange={(next) => setFieldValue(`prompt_fields.${index}.value`, next)}
                disabled={readOnly || inheritsDefault}
                testIdPrefix={`logic-combinator-${index}`}
              />
              {isEmpty(rowLinks) ? (
                <p className="muted-text" data-testid={`logic-no-conditions-${index}`}>
                  {canAddCondition
                    ? 'No conditions yet - add the ones this field combines with its branch button.'
                    : 'No conditions yet.'}
                </p>
              ) : (
                <FieldLinkStrip
                  promptFields={values.prompt_fields}
                  links={rowLinks}
                  optionNames={optionNames}
                  index={index}
                  readOnly={readOnly || !enableFieldTools}
                  chipLabel={null}
                  joiner={LOGIC_COMBINATOR_COPY[promptField.value]?.joiner}
                  onEdit={(link) => setConditionDraft({ field: promptField, link })}
                  canAdd={canAddCondition}
                  onAdd={() => setConditionDraft({ field: promptField })}
                  onRemove={(link) =>
                    setFieldLinks(fieldLinks.filter((candidate) => candidate !== link))
                  }
                />
              )}
            </div>
          ) : (
            <PromptDescriptionField
              name={`prompt_fields.${index}.value`}
              value={promptField.value}
              onCommit={(next) => setFieldValue(`prompt_fields.${index}.value`, next)}
              // History is read-only rather than disabled so its text stays
              // scrollable and selectable (see PromptDescriptionField), and that
              // wins over the inherited/Summary locks — those only matter where a
              // row could still be unlocked and edited.
              disabled={!readOnly && ((isSummary && !isSummaryEditable) || inheritsDefault)}
              readOnly={readOnly}
              placeholder="Provide clear instructions for the specific call element you want to highlight or analyze"
              testId={`prompt_fields.${index}.value`}
              referenceError={
                branchingEnabled
                  ? describeTokenProblems(
                      findTokenProblems(
                        promptField,
                        values.prompt_fields,
                        fieldLinks,
                        loadedFieldNames
                      )
                    )
                  : undefined
              }
              tokenSuggestionsFor={
                branchingEnabled
                  ? (query) =>
                      getTokenSuggestions(promptField, values.prompt_fields, fieldLinks, query)
                  : undefined
              }
              showInheritedTooltip={inheritsDefault}
              inheritedTooltip={INHERITED_FIELD_TOOLTIP}
              {...(isVoipMetadata ? METADATA_PATH_FIELD_PROPS : {})}
            />
          )}
          {promptField.unlockedFromDefault === true && (
            <p className="muted-text rtn-field-note" data-testid={`override-field-note-${index}`}>
              Changes apply only to this configuration, not your organization defaults. Changing the
              name replaces this field with a new one.
            </p>
          )}

          {canConfigureIndividualPrompt && promptField.individualExecution === true && (
            <>
              <div
                className="rtn-individual-execution__model"
                data-testid={`individual-execution-${index}`}
              >
                <label>AI Model</label>
                <Field
                  name={`prompt_fields.${index}.llm_model_id`}
                  component={LlmModelSelect}
                  options={formattedModelOptions}
                  loading={rtnModelOptionsLoading}
                  data-testid={`individual-execution-model-${index}`}
                  isDisabled={readOnly}
                />
              </div>
              {/* A sibling of the model column (not nested in it) so the editor
                  spans the description field's width — see .rtn-pair styling. */}
              <PromptEditorFields
                namePrefix={`prompt_fields.${index}`}
                testId={`rtn-field-prompt-editor-${index}`}
                readOnly={readOnly}
              />
            </>
          )}
        </div>
      </Drag>
    )
  }

  // Forking an inherited taxonomy: the row takes a copy of the options it was
  // resolving against and drops the shared uuid, so the flow save mints a
  // taxonomy of its own for this configuration (see saveBitFlowForPlaybook).
  // Option uuids are deliberately left behind — a fresh taxonomy mints its own —
  // and the inherited uuid is kept so the row can be put back before saving.
  const breakTaxonomyFromDefault = (index, options) => {
    const promptField = values.prompt_fields[index]
    const forkedOptions = options.map((option) => ({
      name: option.name,
      description: option.description ?? '',
    }))
    // No taxonomy and a staged list: the same shape a brand-new option-backed
    // field has, which is what the save path already knows how to mint from. What
    // the row reverts to lives in the loaded flow, not in a snapshot on the row.
    setFieldValue(`prompt_fields.${index}`, {
      ...omit(promptField, ['taxonomy_uuid', 'inheritsTaxonomy']),
      new_taxonomy_options: forkedOptions,
    })

    // The copy isn't a taxonomy yet, so the viewer that loads one by uuid can't
    // show it. Hand the user straight to the editor that can — forking is only
    // ever a step toward changing these options.
    setPickListField(null)
    setCategorizationField(null)
    setStagedOptionsField({
      index,
      fieldName: promptField.key,
      gatedOperands: getGatedOperands(fieldLinks, promptField.key),
      isCategorization: promptField.field_type === 'categorization',
      options: forkedOptions,
    })
  }

  // Adds the field the guided modal just described. Everything the row needs to
  // save as that type travels on it: its data/field type, and — for an
  // option-backed type — the options staged until save mints a taxonomy.
  const handleCreateField = ({ key, value, fieldType, dataType, options, taxonomyUuid }) => {
    const newField = { key, value }
    if (dataType) {
      newField.data_type = dataType
    }
    if (groupByAgentVisibility) {
      newField.show_to_user = addFieldShowToAgent
    }
    if (fieldType && fieldType !== 'extraction') {
      newField.field_type = fieldType
    }
    if (!isNil(taxonomyUuid)) {
      // The name already has options at the org default, and the modal offered
      // them unchanged — so link to that taxonomy rather than minting a second
      // copy of the same list. The field shares it like an inherited one, which
      // is what locks its options here and offers the fork.
      newField.taxonomy_uuid = taxonomyUuid
      newField.inheritsTaxonomy = true
    } else if (!isEmpty(options)) {
      // Staged options — the save path mints a taxonomy from these and links the
      // field to it (a brand-new field has no taxonomy_uuid yet).
      newField.new_taxonomy_options = options
    }
    // In the grouped view, keep the flat order agent-visible-first so the new
    // field's order_id matches its group instead of sorting to the very end.
    setFieldValue(
      'prompt_fields',
      groupByAgentVisibility
        ? appendFieldInVisibilityOrder(values.prompt_fields, newField)
        : [...values.prompt_fields, newField]
    )
    setShowAddField(false)
  }

  const renderAddButton = (showToAgent, dataTestId) => {
    if (readOnly) {
      return null
    }

    return (
      <div className="button-bar">
        <div data-testid={dataTestId} id={dataTestId}>
          <Button
            type="button"
            onClick={() => {
              setAddFieldShowToAgent(groupByAgentVisibility ? showToAgent : null)
              setShowAddField(true)
            }}
            primary
          >
            Add Field
          </Button>
        </div>
      </div>
    )
  }

  // One overlay across every group, not one per group: a Processed Later field
  // may be gated on an agent-visible one, and that connector has to cross the
  // group boundary to be drawn at all.
  const renderRail = () => (
    <ConnectionRail
      containerRef={fieldListRef}
      promptFields={values.prompt_fields}
      links={shownRailLinks}
      focusedKey={focusedFieldKey}
      revision={values.prompt_fields.map((field) => field.key).join('|')}
    />
  )

  const renderRows = () => {
    if (groupByAgentVisibility) {
      const partitioned = partitionFieldsByAgentVisibility(values.prompt_fields)

      return (
        <FieldArray
          name="prompt_fields"
          render={(arrayHelpers) => (
            <DragAndDrop onDragEnd={handleReorder}>
              <div className="rtn-field-list" ref={fieldListRef}>
                {renderRail()}
                {VISIBILITY_GROUPS.map((group) => (
                  <div
                    className="rtn-field-group"
                    key={group.id}
                    data-testid={`field-group-${group.id}`}
                  >
                    <h3>{group.title}</h3>
                    <p className="muted-text">{group.description}</p>
                    <Drop droppableId={group.id}>
                      {partitioned[group.id].map(({ field, index }, position) =>
                        renderRow(arrayHelpers, field, index, position)
                      )}
                    </Drop>
                    {renderAddButton(
                      group.id === FIELD_GROUP.SHOWN_TO_AGENT,
                      `add-row-button-${group.id}`
                    )}
                  </div>
                ))}
              </div>
            </DragAndDrop>
          )}
        />
      )
    }

    return (
      <FieldArray
        name="prompt_fields"
        render={(arrayHelpers) => (
          <DragAndDrop onDragEnd={handleReorder}>
            <div className="rtn-field-list" ref={fieldListRef}>
              {renderRail()}
              <Drop droppableId="prompt-fields">
                {values.prompt_fields.map((promptField, index) =>
                  renderRow(arrayHelpers, promptField, index, index)
                )}
                {renderAddButton(false, 'add-row-button')}
              </Drop>
            </div>
          </DragAndDrop>
        )}
      />
    )
  }

  // None of these modals belongs on a read-only view — they create fields, edit
  // the options a field resolves against, or copy a field onward — so loading an
  // older version closes whichever was open rather than leaving it able to write
  // to a form nothing can save.
  return (
    <>
      {renderRows()}

      <AddFieldModal
        show={showAddField && !readOnly}
        onClose={() => setShowAddField(false)}
        onCreate={handleCreateField}
        fieldNameOptions={addFieldNameOptions}
        usedFieldNames={usedFieldNames}
        // Lets a chosen name that already exists as an org default start from
        // what that default is: its type, its description, and its options.
        defaultFieldsByFieldName={defaultFieldsByName}
        // Lets the modal dry-run a metadata field against this playbook's most
        // recent call before adding it. The org defaults editor has no playbook
        // to test against, so there the field is added unverified.
        playbookCid={values.selected_playbook_cid}
        organizationId={taxonomyOrgId}
        // A logic field is defined by its conditions, so it's only offered
        // where conditions can be added.
        allowLogic={enableFieldTools && branchingEnabled}
      />

      {enableFieldTools && conditionDraft && !readOnly && (
        <FieldConditionModal
          show
          onClose={() => setConditionDraft(null)}
          targetField={conditionDraft.field}
          existingLink={conditionDraft.link}
          sourceOptions={getEligibleSources(values.prompt_fields, shownLinks, conditionDraft.field)}
          inheritedTaxonomyKeys={inheritedTaxonomyKeys}
          pendingTaxonomies={pendingTaxonomies}
          onSave={(link) => {
            setFieldLinks(
              isNil(conditionDraft.link)
                ? addLink(fieldLinks, link)
                : fieldLinks.map((candidate) =>
                    candidate === conditionDraft.link ? link : candidate
                  )
            )
            setConditionDraft(null)
          }}
          onRemove={() => {
            setFieldLinks(fieldLinks.filter((candidate) => candidate !== conditionDraft.link))
            setConditionDraft(null)
          }}
        />
      )}

      {stagedOptionsField && !readOnly && (
        <StagedOptionsModal
          show
          onClose={() => setStagedOptionsField(null)}
          fieldName={stagedOptionsField.fieldName}
          gatedOperands={stagedOptionsField.gatedOperands}
          isCategorization={stagedOptionsField.isCategorization}
          options={stagedOptionsField.options}
          onSave={(nextOptions) =>
            setFieldValue(
              `prompt_fields.${stagedOptionsField.index}.new_taxonomy_options`,
              nextOptions
            )
          }
        />
      )}

      {enableFieldTools && copyField && !readOnly && (
        <CopyFieldDescriptionModal
          show={Boolean(copyField)}
          onClose={() => setCopyField(null)}
          fieldName={copyField.fieldName}
          fieldId={copyField.fieldId}
          sourceFlowUuid={rtnBitFlow?.flow_uuid}
          playbookOptions={copyTargetOptions}
        />
      )}

      {pickListField && !readOnly && (
        <PickListOptionsModal
          show={Boolean(pickListField)}
          onClose={() => setPickListField(null)}
          fieldName={pickListField.fieldName}
          pendingEdit={pendingTaxonomies[pickListField.taxonomyUuid]}
          onSaved={(saved) => handleTaxonomySaved(pickListField.taxonomyUuid, saved)}
          gatedOperands={pickListField.gatedOperands}
          taxonomyUuid={pickListField.taxonomyUuid}
          organizationId={taxonomyOrgId}
          inheritedFromDefault={pickListField.inheritsTaxonomy}
          appliesToInheritingPlaybooks={isOrgDefaultFlow}
          onLinkToDefault={
            isNil(pickListField.defaultTaxonomyUuid)
              ? undefined
              : () => linkTaxonomyToDefault(pickListField.index, pickListField.defaultTaxonomyUuid)
          }
          onBreakFromDefault={(options) => breakTaxonomyFromDefault(pickListField.index, options)}
          onApplyForTesting={
            enableFieldTools && pickListField.inlineTestable
              ? (taxonomyEdit) => applyPendingTaxonomy(pickListField.taxonomyUuid, taxonomyEdit)
              : undefined
          }
        />
      )}

      {categorizationField && !readOnly && (
        <CategorizationOptionsModal
          show={Boolean(categorizationField)}
          onClose={() => setCategorizationField(null)}
          fieldName={categorizationField.fieldName}
          pendingEdit={pendingTaxonomies[categorizationField.taxonomyUuid]}
          onSaved={(saved) => handleTaxonomySaved(categorizationField.taxonomyUuid, saved)}
          gatedOperands={categorizationField.gatedOperands}
          taxonomyUuid={categorizationField.taxonomyUuid}
          organizationId={taxonomyOrgId}
          inheritedFromDefault={categorizationField.inheritsTaxonomy}
          appliesToInheritingPlaybooks={isOrgDefaultFlow}
          onLinkToDefault={
            isNil(categorizationField.defaultTaxonomyUuid)
              ? undefined
              : () =>
                  linkTaxonomyToDefault(
                    categorizationField.index,
                    categorizationField.defaultTaxonomyUuid
                  )
          }
          onBreakFromDefault={(options) =>
            breakTaxonomyFromDefault(categorizationField.index, options)
          }
          onApplyForTesting={
            enableFieldTools
              ? (taxonomyEdit) =>
                  applyPendingTaxonomy(categorizationField.taxonomyUuid, taxonomyEdit)
              : undefined
          }
        />
      )}
    </>
  )
}
